import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Dropdown, Badge, Spinner, Button, ListGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/adminService';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
}

const TYPE_META: Record<string, { icon: string; color: string }> = {
  refund_request: { icon: 'bi-arrow-counterclockwise', color: 'warning' },
  payment_failed: { icon: 'bi-exclamation-triangle', color: 'danger' },
  server_error: { icon: 'bi-bug', color: 'danger' },
  movie_approval: { icon: 'bi-film', color: 'info' },
  settings_change: { icon: 'bi-gear', color: 'secondary' },
  booking_fee_tier_change: { icon: 'bi-cash-coin', color: 'secondary' },
};

const POLL_INTERVAL_MS = 30000;

const NotificationDropdown: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const intervalRef = useRef<number | null>(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await adminService.getUnreadNotificationCount();
      if (res.success && res.data) {
        setUnreadCount(Number(res.data.count || 0));
      }
    } catch {
      // Silent — polling is best-effort
    }
  }, []);

  const fetchRecent = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminService.getAdminNotifications({ limit: 8, page: 1 });
      if (res.success && res.data) {
        setNotifications(res.data);
        setUnreadCount(res.data.filter((n: Notification) => !n.isRead).length);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    intervalRef.current = window.setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (open) {
      fetchRecent();
    }
  }, [open, fetchRecent]);

  const handleMarkRead = async (id: string) => {
    try {
      await adminService.markNotificationRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await adminService.markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
    }
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate('/notification-center');
  };

  const timeAgo = (iso: string) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (isNaN(date.getTime())) return '';
    const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diffSec < 60) return 'just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hr ago`;
    return `${Math.floor(diffSec / 86400)} d ago`;
  };

  return (
    <Dropdown show={open} onToggle={(next) => setOpen(next)} align="end">
      <Dropdown.Toggle
        as={Button}
        variant="outline-secondary"
        size="sm"
        className="position-relative"
        aria-label="Notifications"
      >
        <i className="bi-bell"></i>
        {unreadCount > 0 && (
          <Badge
            bg="danger"
            className="position-absolute top-0 start-100 translate-middle rounded-pill"
            style={{ fontSize: '0.65rem', minWidth: 18 }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu
        style={{ minWidth: 360, maxWidth: 380, maxHeight: 480, overflow: 'hidden' }}
        className="p-0"
      >
        <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
          <div>
            <strong>Notifications</strong>
            {unreadCount > 0 && (
              <Badge bg="primary" className="ms-2">
                {unreadCount} new
              </Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="link"
              size="sm"
              className="p-0 text-decoration-none"
              onClick={handleMarkAllRead}
            >
              Mark all read
            </Button>
          )}
        </div>

        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center text-muted py-4">
              <i className="bi-bell-slash fs-1 opacity-25 d-block mb-2"></i>
              <small>No notifications yet</small>
            </div>
          ) : (
            <ListGroup variant="flush">
              {notifications.map((n) => {
                const meta = TYPE_META[n.type] || {
                  icon: 'bi-info-circle',
                  color: 'info',
                };
                return (
                  <ListGroup.Item
                    key={n.id}
                    action
                    onClick={() => !n.isRead && handleMarkRead(n.id)}
                    className={`py-2 px-3 ${!n.isRead ? 'bg-light' : ''}`}
                    style={{ borderLeft: !n.isRead ? '3px solid #0d6efd' : '3px solid transparent' }}
                  >
                    <div className="d-flex align-items-start">
                      <i
                        className={`bi ${meta.icon} text-${meta.color} me-2 mt-1`}
                        style={{ fontSize: '1.1rem' }}
                      ></i>
                      <div className="flex-grow-1 overflow-hidden">
                        <div
                          className="fw-medium text-truncate"
                          style={{ fontSize: '0.875rem' }}
                        >
                          {n.title}
                        </div>
                        <div
                          className="text-muted text-truncate"
                          style={{ fontSize: '0.75rem' }}
                        >
                          {n.message}
                        </div>
                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                          {timeAgo(n.createdAt)}
                        </small>
                      </div>
                    </div>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          )}
        </div>

        <div className="border-top px-3 py-2 text-center">
          <Button
            variant="link"
            size="sm"
            className="text-decoration-none"
            onClick={handleViewAll}
          >
            View all notifications
          </Button>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default NotificationDropdown;
